import { inject, injectable } from 'inversify';
import dayjs from 'dayjs';
import { InjectionSymbols, type Logger, LogType } from '@hitiq/logger';
import { ViewIncidentQueryService } from '../../../pages/viewIncident/viewIncident.queryParam.service';
import { useAppSelector } from '../../../state/hooks';
import {
  AccountsRepository,
  ConneqtRepository,
  PlayerRepository,
} from '../../../state/reduxRepositories';
import { AccountTypes } from '../../../components/changeUser/accountTypes.enum';
import { AuthService } from '../../../state/helperServices/auth/auth.service';
import { Account } from '../../../state/reduxRepositories/accounts/accounts.interfaces';
import { createDeepEqualSelector } from '../../../state/selector.helpers';
import { LoggedInUser } from '../../../state/helperServices/auth/auth.interfaces';
import { Player } from '../../../state/reduxRepositories/players/player.interfaces';
import {
  Conneqt,
  ConneqtStatus,
} from '../../../state/reduxRepositories/conneqt/conneqt.interfaces';

@injectable()
export class AppointmentDomain {
  private clinicAccountSelector: any;
  private playerSelector: any;
  private clinicConneqtForPlayerSelector: any;

  constructor(
    private viewIncidentQueryService: ViewIncidentQueryService,
    private accountsRepository: AccountsRepository,
    private playerRepository: PlayerRepository,
    private conneqtRepository: ConneqtRepository,
    private authService: AuthService,
    @inject(InjectionSymbols.LOGGER) private logger: Logger,
  ) {
    this.createClinicAccountSelector();
    this.createPlayerSelector();
    this.createClinicConneqtForPlayerSelector();
  }

  useUserSelector = (): LoggedInUser => {
    return useAppSelector(this.authService.loggedInUserSelector);
  };

  useSelectPlayerId = () => {
    return useAppSelector(this.viewIncidentQueryService.mergedQuerySelector)
      .selectedPlayerId;
  };

  usePlayerSelector = (): Player | undefined => {
    return useAppSelector(this.playerSelector);
  };

  useClinicAccountSelector = (): Account | undefined => {
    return useAppSelector(this.clinicAccountSelector);
  };

  useConneqtErrorsSelector = () => {
    return useAppSelector(this.conneqtRepository.selectors.errorsSelector);
  };

  useClinicConneqtForPlayerSelector = (): Conneqt | undefined => {
    return useAppSelector(this.clinicConneqtForPlayerSelector);
  };

  dispatchConneqtPlayerWithClinic = (
    player: Player,
    clinicAccount: Account,
  ) => {
    this.conneqtRepository.dispatchCreate({
      requestPlayerId: player.id,
      requestPlayerClues: `${player?.firstName} ${player?.lastName},${dayjs(
        player?.dateOfBirth,
      ).format('DD/MM/YYYY')}`,
      status: ConneqtStatus.ClinicAppointmentBooked,
      responseAccountId: clinicAccount?.id,
      responseAccountType: AccountTypes.Clinic,
      responseAccountName: clinicAccount.name,
      approvedDate: new Date().toISOString(),
    });
  };

  private getDevClinic = (clinicAccounts: Account[], user: LoggedInUser) => {
    const clinicAccount = clinicAccounts.find((_account) => {
      const accountName = _account.name ? _account.name.split('+')[0] : '';
      const loggedInUserName = user?.email?.split('+')[0];
      return accountName === loggedInUserName;
    });

    if (clinicAccount) {
      return clinicAccount;
    }
    this.logger.log(
      LogType.debug,
      `BookAppointment: You need to register a clinic using the dev login`,
    );
    // don't select firstClinic in dev since this will send it to the dev that signed up first. Instead let it spin.
    return undefined;
  };

  private createClinicAccountSelector = () => {
    this.clinicAccountSelector = createDeepEqualSelector(
      this.accountsRepository.selectors.resourcesSelector,
      this.authService.loggedInUserSelector,
      (accounts: Account[], user: LoggedInUser | undefined) => {
        const clinicAccounts = accounts.filter(
          (account) => account.type === AccountTypes.Clinic,
        );

        if (clinicAccounts?.length === 1) {
          return clinicAccounts[0];
        }

        if (process.env.NODE_ENV === 'development') {
          return this.getDevClinic(clinicAccounts, user as LoggedInUser);
        }

        this.logger.log(
          LogType.error,
          `BookAppointment: Should only be exactly one clinic account found when booking appointment in test and prod. clinicAccounts?.length: ${clinicAccounts?.length}`,
        );

        // return first created clinic so test and prod doesn't fail if someone accidentally creates another clinic. But also alert above so we can fix it or add a select input to let the user choose if it is intentional.
        const firstClinic = clinicAccounts.reduce((prev, current) => {
          const prevDate = new Date(prev.createdAt);
          const currentDate = new Date(current.createdAt);
          return prevDate < currentDate ? prev : current;
        });

        return firstClinic;
      },
    );
  };

  private createPlayerSelector = () => {
    this.playerSelector = createDeepEqualSelector(
      this.playerRepository.selectors.resourcesSelector,
      this.viewIncidentQueryService.mergedQuerySelector,
      (players, queryParam) => {
        return players.find((p) => p.id === queryParam.selectedPlayerId);
      },
    );
  };

  private createClinicConneqtForPlayerSelector = () => {
    this.clinicConneqtForPlayerSelector = createDeepEqualSelector(
      this.conneqtRepository.selectors.resourcesSelector,
      this.viewIncidentQueryService.mergedQuerySelector,
      (conneqts, queryParam) => {
        const clinicConneqts = conneqts.filter(
          (conneqt) => conneqt.responseAccountType === AccountTypes.Clinic,
        );

        if (clinicConneqts?.length === 0) {
          return undefined;
        }

        const clinicConneqtsForPlayer = clinicConneqts.find(
          (conneqt) => conneqt.requestPlayerId === queryParam.selectedPlayerId,
        );

        return clinicConneqtsForPlayer;
      },
    );
  };
}
